import React from 'react';

/**
 * Curated icon registry — only icons referenced in the codebase are imported.
 *
 * Why: `import * as LucideIcons from 'lucide-react'` pulls every icon (~1500),
 * adding 800 KB of bundle weight. Direct named imports are tree-shakeable, so
 * only the icons listed below ship.
 *
 * To add a new icon: append it to the named import below. The `<Icon name="X" />`
 * API stays unchanged — looks up here instead of the full lucide namespace.
 */
import {
  Activity, AlertCircle, AlertTriangle, Archive, ArrowLeft, ArrowRight, ArrowUp,
  BadgeCheck, BadgeIndianRupee, BarChart3, Bath, Bed, Bell, BellOff, Bike, Book, BookOpen,
  Brain, Briefcase, Building, Building2, Bus,
  Calculator, Calendar, CalendarCheck, CalendarPlus, Camera, Car, ChefHat,
  Check, CheckCheck, CheckCircle, CheckCircle2,
  ChevronDown, ChevronLeft, ChevronRight, Chrome, Clock, Coffee, CreditCard,
  DoorOpen, Download, Dumbbell, Edit, Edit2, Edit3, ExternalLink, Eye,
  EyeOff, Facebook, FileCheck, FileEdit, FileText, Filter, Flag, Flame, Flower,
  GitCompare, Globe,
  Heart, HelpCircle, Home,
  Image, IndianRupee, Info, Instagram,
  Key, Lightbulb, Linkedin, List, Loader, Lock, LogOut,
  Mail, Map, MapPin, Menu,
  MessageCircle, MessageCircleMore, MessageSquare, MessageSquarePlus,
  Minus, MoreVertical,
  Navigation, Locate, Compass,
  Paperclip, PenTool, Phone, PhoneCall, Play, Plus, Quote,
  RefreshCw, Rocket, Save, Scale, Search, SearchX, Send, Settings,
  Share, Share2, Shield, ShieldAlert, ShieldCheck, Shirt, Smartphone, Smile,
  Snowflake, Sparkles, Square, Star, Store, Sun,
  Tag, Thermometer, ThumbsDown, ThumbsUp, Train, Trash2,
  TreePine, Trees, TrendingDown, TrendingUp, Tv, Twitter,
  Upload, User, UserCheck, UserX, Users, Utensils,
  Video, Warehouse, Waves, Wifi, Wrench,
  X, XCircle, Youtube, Zap, ZoomIn,
} from 'lucide-react';

const WhatsAppIcon = ({ size = 24, color = 'currentColor', className = '', ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    {...props}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const REGISTRY = {
  Activity, AlertCircle, AlertTriangle, Archive, ArrowLeft, ArrowRight, ArrowUp,
  BadgeCheck, BadgeIndianRupee, BarChart3, Bath, Bed, Bell, BellOff, Bike, Book, BookOpen,
  Brain, Briefcase, Building, Building2, Bus,
  Calculator, Calendar, CalendarCheck, CalendarPlus, Camera, Car, ChefHat,
  Check, CheckCheck, CheckCircle, CheckCircle2,
  ChevronDown, ChevronLeft, ChevronRight, Chrome, Clock, Coffee, CreditCard,
  DoorOpen, Download, Dumbbell, Edit, Edit2, Edit3, ExternalLink, Eye,
  EyeOff, Facebook, FileCheck, FileEdit, FileText, Filter, Flag, Flame, Flower,
  GitCompare, Globe,
  Heart, HelpCircle, Home,
  Image, IndianRupee, Info, Instagram,
  Key, Lightbulb, Linkedin, List, Loader, Lock, LogOut,
  Mail, Map, MapPin, Menu,
  MessageCircle, MessageCircleMore, MessageSquare, MessageSquarePlus,
  Minus, MoreVertical,
  Navigation, Locate, Compass,
  Paperclip, PenTool, Phone, PhoneCall, Play, Plus, Quote,
  RefreshCw, Rocket, Save, Scale, Search, SearchX, Send, Settings,
  Share, Share2, Shield, ShieldAlert, ShieldCheck, Shirt, Smartphone, Smile,
  Snowflake, Sparkles, Square, Star, Store, Sun,
  Tag, Thermometer, ThumbsDown, ThumbsUp, Train, Trash2,
  TreePine, Trees, TrendingDown, TrendingUp, Tv, Twitter,
  Upload, User, UserCheck, UserX, Users, Utensils,
  Video, Warehouse, Waves, Wifi, Wrench,
  WhatsApp: WhatsAppIcon,
  Whatsapp: WhatsAppIcon,
  X, XCircle, Youtube, Zap, ZoomIn,
};

function Icon({
  name,
  size = 24,
  color = 'currentColor',
  className = '',
  strokeWidth = 2,
  ...props
}) {
  const IconComponent = REGISTRY[name];
  if (!IconComponent) {
    if (process.env.NODE_ENV !== 'production') {
      // Helps catch typos / missing-from-registry icons during dev.
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Unknown icon "${name}". Add it to AppIcon.jsx REGISTRY.`);
    }
    return (
      <HelpCircle
        size={size}
        color="currentColor"
        strokeWidth={strokeWidth}
        className={className}
        {...props}
      />
    );
  }
  return (
    <IconComponent
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      {...props}
    />
  );
}
export default Icon;
